import {
  MutationOptions,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { useApi } from "@platform/api";

import { AUTO_HIDE_DURATION_MS } from "../constants";
import { AtLeastOne } from "../types/utils";
import { extractErrorMessage } from "../utils";

import useToastNotificationService from "./useToastNotificationService";

type UpdateRfpVariables = AtLeastOne<{
  carriers: string[];
  status: string;
}>;

interface UseUpdateRfpOptions<TContext = unknown> extends Pick<
  MutationOptions<void, Error, UpdateRfpVariables, TContext>,
  "onSuccess" | "onError" | "onMutate" | "onSettled"
> {
  rfpId: string;
  successMessage?: string;
  errorMessage?: string;
}

function useUpdateRfp<TContext = unknown>({
  rfpId,
  successMessage,
  errorMessage,
  onError,
  onSuccess,
  ...params
}: UseUpdateRfpOptions<TContext>): UseMutationResult<
  void,
  Error,
  UpdateRfpVariables,
  TContext
> {
  const axiosInstance = useApi();
  const { createToastNotification } = useToastNotificationService();

  return useMutation<void, Error, UpdateRfpVariables, TContext>({
    mutationFn: (body) =>
      axiosInstance.patch(`brokerportal/v1/rfp/${rfpId}`, body),
    onSuccess: (...params) => {
      onSuccess?.(...params);
      if (successMessage) {
        createToastNotification({
          message: successMessage,
          severity: "success",
          autoHideDuration: AUTO_HIDE_DURATION_MS,
        });
      }
    },
    onError: (error, ...errorParams) => {
      onError?.(error, ...errorParams);
      createToastNotification({
        message: errorMessage ?? extractErrorMessage(error),
        severity: "error",
        autoHideDuration: AUTO_HIDE_DURATION_MS,
      });
    },
    ...params,
  });
}

export { useUpdateRfp };
